import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChatText, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Bidirectional job messages thread — worker <-> office.
 * Polls every 15 seconds for new messages. Also renders any access request
 * status inline (kind === "access_request" or "access_response").
 */
export default function JobMessages({ jobId, isAdmin, onAccessResolveClick }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const lastAt = useRef(null);
  const listRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/messages`);
      setMsgs(data);
      if (data.length) lastAt.current = data[data.length - 1].created_at;
    } catch (e) { /* silent poll */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/messages`, { text: text.trim() });
      setMsgs(prev => [...prev, data.message]);
      setText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally { setBusy(false); }
  };

  const bubbleForKind = (m) => {
    if (m.kind === "access_request") {
      return (
        <div className="border-l-4 border-red-500 pl-2 py-1 bg-red-50 rounded-r">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-700">🔐 Access Request</div>
          <div className="text-sm font-bold text-red-900">{m.text}</div>
          {isAdmin && onAccessResolveClick && (
            <button onClick={() => onAccessResolveClick(m.access_request_id)}
              data-testid={`msg-resolve-${m.access_request_id}`}
              className="mt-1 h-7 px-2 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded">
              Mark Resolved
            </button>
          )}
        </div>
      );
    }
    if (m.kind === "access_response") {
      return (
        <div className="border-l-4 border-emerald-500 pl-2 py-1 bg-emerald-50 rounded-r">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">✅ Resolved</div>
          <div className="text-sm text-emerald-900">{m.text}</div>
        </div>
      );
    }
    const mine = m.author_role === (isAdmin ? "admin" : "worker");
    return (
      <div className={`px-3 py-2 rounded-2xl max-w-[80%] ${
        mine ? "bg-[#00C4CC] text-white ml-auto" : "bg-slate-100 text-slate-900"
      }`}>
        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
          {m.author_name || (mine ? "You" : "—")}{m.author_role === "admin" ? " · Office" : ""}
        </div>
        <div className="text-sm whitespace-pre-wrap">{m.text}</div>
      </div>
    );
  };

  return (
    <div className="mt-4 rugged-card p-4" data-testid="job-messages-panel">
      <div className="flex items-center gap-2 mb-3">
        <ChatText weight="bold" size={18} className="text-[#00C4CC]"/>
        <div className="font-display font-black text-sm uppercase tracking-wider">
          Field ↔ Office <span className="text-slate-400">· {msgs.length}</span>
        </div>
      </div>

      <div ref={listRef} className="max-h-72 overflow-y-auto space-y-2 mb-3 pr-1" data-testid="job-messages-list">
        {msgs.length === 0 ? (
          <div className="text-xs text-slate-400 italic text-center py-4">
            No messages yet. Type below to start the conversation.
          </div>
        ) : msgs.map(m => (
          <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${m.author_role === (isAdmin ? "admin" : "worker") ? "justify-end" : ""}`}>
            {bubbleForKind(m)}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid="msg-input" placeholder="Message the office / crew…"
          maxLength={1000}
          className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm"/>
        <button onClick={send} disabled={busy || !text.trim()} data-testid="msg-send"
          className="tactile-btn h-11 px-4 bg-[#00C4CC] hover:bg-[#00a8af] text-white disabled:opacity-50">
          <PaperPlaneTilt weight="bold" size={18}/>
        </button>
      </div>
    </div>
  );
}
